// src\themes\lino\components\EditorWidget.tsx
'use client';

import { useMemo } from 'react';
import { motion } from 'motion/react';
import { textGroupVariants, textItemVariants } from './animations/textReveal';

// محتوای تولیدشده توسط ادیتور معمولاً یه رشته HTML تخت از تگ‌های سطح-بالاست
// (پاراگراف، تیتر، لیست و ...). برای اینکه انیمیشن ورود متن واقعاً «خط به خط»
// باشه (نه یک بلوک یک‌دست)، این تابع محتوا رو بر اساس تگ‌های سطح-بالا به چند
// قطعه تقسیم می‌کنه تا هر قطعه جدا انیمیت بشه. اگه محتوا قابل تقسیم نبود
// (مثلاً فقط متن ساده یا تگ‌های تودرتوی غیرمعمول)، کل محتوا به‌عنوان یه
// بلوک واحد برمی‌گرده، دقیقاً مثل رفتار قبلی.
const BLOCK_TAG_REGEX = /<(p|h1|h2|h3|h4|h5|h6|ul|ol|blockquote|table|figure|div|pre)[^>]*>[\s\S]*?<\/\1>/gi;

function splitIntoBlocks(html: string): string[] {
    if (!html) return [];
    const matches = html.match(BLOCK_TAG_REGEX);
    if (!matches || matches.length < 2) return [html];

    // اگه بین قطعه‌های پیدا شده متن قابل‌توجهی (غیر از فاصله/تگ) جا مونده باشه
    // یعنی ساختار محتوا با فرض «تگ‌های سطح-بالای تخت» جور درنمیاد؛ برای امنیت
    // به همون رفتار قبلی (یک بلوک) برمی‌گردیم.
    const rejoined = matches.join('');
    const leftover = html.replace(BLOCK_TAG_REGEX, '').replace(/\s|&nbsp;/g, '');
    if (!rejoined || leftover.length > 0) return [html];

    return matches;
}

// وقتی animateText=true باشه (فعلاً فقط صفحه اصلی)، تیتر و محتوا به سبک اپل
// با یه تاخیر کوتاه نسبت به هم و به‌صورت خط‌به‌خط وارد صفحه می‌شن. جای دیگه‌ای
// که این پراپ پاس داده نشه، دقیقاً همون رفتار قبلی (بدون انیمیشن متن) حفظ می‌شه.
export default function EditorWidget({ data, animateText = false }: { data: any; animateText?: boolean }) {

    const content =
        data.content ||
        data.content_en ||
        data.content_ar ||
        (data.items && data.items[0] && data.items[0].content) ||
        "";

    const contentBlocks = useMemo(() => splitIntoBlocks(content), [content]);

    if (!content) return null;

    const textAlign =
        data.direction === "center"
            ? "text-center"
            : data.direction === "right"
                ? "text-end"
                : "text-start";

    const widthMap: Record<string, string> = {
        "1/1": "col-span-12",
        "1/2": "col-span-12 md:col-span-6",
        "1/3": "col-span-12 md:col-span-4",
        "1/4": "col-span-12 md:col-span-3",
        "2/3": "col-span-12 md:col-span-8",
        "3/4": "col-span-12 md:col-span-9",
    };

    const width = widthMap[data.width || "1/1"];

    if (!animateText) {
        return (
            <div
                className={`
                    ${width}
                    w-full
                    h-full
                    flex flex-col
                    space-y-4
                    ${textAlign}
                `}
            >
                {data.title && (
                    <h2 className="text-lg lg:text-xl font-light text-primary-900 tracking-tight">
                        {data.title}
                    </h2>
                )}

                {content && (
                    <div
                        className="
                            text-primary-600
                            text-[13px]
                            leading-6
                            prose max-w-none
                            whitespace-pre-line
                        "
                        dangerouslySetInnerHTML={{ __html: content }}
                    />
                )}
            </div>
        );
    }

    return (
        <motion.div
            className={`
                ${width}
                w-full
                h-full
                flex flex-col
                space-y-4
                ${textAlign}
            `}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.4 }}
            variants={textGroupVariants}
        >
            {data.title && (
                <motion.h2 variants={textItemVariants} className="text-lg lg:text-xl font-light text-primary-900 tracking-tight">
                    {data.title}
                </motion.h2>
            )}

            {contentBlocks.length > 0 && (
                <motion.div
                    variants={textGroupVariants}
                    className="
                        text-primary-600
                        text-[13px]
                        leading-6
                        prose max-w-none
                        whitespace-pre-line
                    "
                >
                    {contentBlocks.map((block, index) => (
                        <motion.div
                            key={index}
                            variants={textItemVariants}
                            dangerouslySetInnerHTML={{ __html: block }}
                        />
                    ))}
                </motion.div>
            )}
        </motion.div>
    );
}